<template>
  <div class="p-6">
    <!-- Title -->
    <div class="mb-5">
      <h2 class="text-lg font-bold">
        {{ $t('W: Annuller ordre') }}
        <span v-if="order">#{{ order.orderNumber }}</span>
      </h2>
      <p class="mt-1 text-sm">
        {{
          $t(
            'W: Vælg om du vil annullere hele ordren eller kun specifikke prøver.'
          )
        }}
      </p>
    </div>

    <!-- Tab toggle -->
    <div class="mb-5 flex gap-2">
      <span
        v-tooltip="
          hasLockedGroup
            ? $t('W: Ikke muligt — en eller flere rapporter er godkendt eller afsendt')
            : ''
        ">
        <button
          type="button"
          :disabled="hasLockedGroup"
          :class="[
            'rounded-md px-4 py-2 text-sm font-medium transition-colors',
            hasLockedGroup
              ? 'cursor-not-allowed border border-border-subtle text-text-muted'
              : mode === 'entire'
                ? 'border-2 border-feedback-error text-feedback-error'
                : 'border border-border-subtle hover:border-border-strong',
          ]"
          @click="mode = 'entire'">
          {{ $t('W: Hele ordren') }}
        </button>
      </span>
      <button
        type="button"
        :class="[
          'rounded-md px-4 py-2 text-sm font-medium transition-colors',
          mode === 'samples'
            ? 'border-2 border-feedback-error text-feedback-error'
            : 'border border-border-subtle hover:border-border-strong',
        ]"
        @click="mode = 'samples'">
        {{ $t('W: Udvalgte prøver') }}
      </button>
    </div>

    <!-- Sample selection (mode === 'samples') -->
    <div
      v-if="mode === 'samples'"
      class="mb-5 overflow-hidden rounded-lg border border-border-subtle">
      <!-- Select all row -->
      <label
        class="bg-surface-muted flex cursor-pointer items-center gap-3 px-4 py-2.5">
        <input
          type="checkbox"
          class="accent-feedback-error h-4 w-4 cursor-pointer"
          :checked="allSelected"
          @change="onToggleAll" />
        <span class="text-xs font-semibold tracking-wider uppercase">
          {{ $t('W: Vælg alle') }}
        </span>
      </label>
      <!-- Sample rows -->
      <label
        v-for="item in cancellableSamples"
        :key="item.sample.sampleID"
        class="hover:bg-surface-muted flex cursor-pointer items-center gap-3 border-t border-border-subtle px-4 py-2.5">
        <input
          type="checkbox"
          class="accent-feedback-error h-4 w-4 cursor-pointer"
          :checked="selectedSampleIDs.includes(item.sample.sampleID)"
          @change="onToggleSample(item.sample.sampleID)" />
        <span class="text-sm font-semibold text-text-primary">
          {{ getSampleLabel(item.sample.sampleNumber, currentLocale) }}
        </span>
        <span class="text-xs">
          {{ item.sample.label.id }}
        </span>
      </label>
    </div>

    <!-- Reason -->
    <div class="mb-5">
      <label
        class="mb-1.5 block text-xs font-semibold tracking-wider uppercase">
        {{ $t('W: Årsag') }}
        <span class="font-normal normal-case">({{ $t('W: valgfri') }})</span>
      </label>
      <textarea
        v-model="reason"
        rows="3"
        :placeholder="$t('W: f.eks. Kundeforespørgsel, dublet ordre...')"
        class="w-full resize-none rounded-md border border-border-subtle px-3 py-2 text-sm focus:border-border-strong focus:outline-none" />
    </div>

    <!-- Error -->
    <p
      v-if="errorText"
      class="text-feedback-error mb-4 text-sm">
      {{ errorText }}
    </p>

    <!-- Buttons -->
    <div class="flex justify-end gap-3">
      <UiBaseButton
        :text="$t('W: Behold ordre')"
        theme="neutral"
        @button-click="$emit('close')" />
      <UiBaseButton
        :text="submitLabel"
        :isLoading="isLoading"
        :showSaveIcon="showSaveIcon"
        :isDisabled="isSubmitDisabled"
        @button-click="onSubmit" />
    </div>
  </div>
</template>

<script lang="ts">
import { isDeliveryReportLocked } from '@team-milva/lab-utils/orderDeliveries'
import { getOrderInvoiceLock } from '@team-milva/lab-utils/orderInvoiceBlocking'
import { useInvoiceStore } from '@/stores/invoiceStore'
import { useOrderStore } from '@/stores/orderStore'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { useAppStore } from '@/stores/appStore'
import { useUserStore } from '@/stores/userStore'
import { getSampleLabel } from '@/globals/typescript/utils/sampleLabel'
import type { IOrderSample } from '@team-milva/lab-models/resources'
import { mapActions, mapState } from 'pinia'
import { useToast } from '@/globals/typescript/utils/toast'

export default {
  name: 'CancelOrderModal',

  emits: ['close'],

  props: {
    orderID: { type: String, required: true },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      modalEntryID: useModalStore().top?.id,

      mode: 'entire' as 'entire' | 'samples',
      selectedSampleIDs: [] as string[],
      reason: '',
      isLoading: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useInvoiceStore, ['invoicesListByOrderID']),
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),

    order() {
      return this.allOrders.find((x) => x.id === this.orderID)
    },

    // Whole-order cancellation requires every group to be unlocked — matches
    // cancelOrderService's own check. Selecting individual samples remains
    // available regardless, scoped per-sample via cancellableSamples below.
    hasLockedGroup(): boolean {
      if (!this.order) return false
      return Object.values(this.order.tatGroups).some((group) =>
        isDeliveryReportLocked(group.statuses.labReportStatus)
      )
    },

    cancellableSamples(): { index: number; sample: IOrderSample }[] {
      if (!this.order) return []
      return this.order.samples
        .map((sample, index) => ({ index, sample }))
        .filter(
          (x) =>
            x.sample.status !== 'cancelled' &&
            !this.isSampleReportLocked(x.sample)
        )
    },

    allSelected(): boolean {
      return (
        this.cancellableSamples.length > 0 &&
        this.cancellableSamples.every((x) =>
          this.selectedSampleIDs.includes(x.sample.sampleID)
        )
      )
    },

    isSubmitDisabled(): boolean {
      // Both modes (entire order / selected samples) are gated by the single
      // orders.cancel permission — this modal is only ever reachable via
      // already-gated entry points, but this check is kept as defense in depth.
      if (!this.canUser('orders.cancel')) return true
      if (this.mode === 'entire') return this.hasLockedGroup
      return this.selectedSampleIDs.length === 0
    },

    submitLabel(): string {
      if (this.mode === 'entire') {
        return this.$t('W: Annuller hele ordren')
      }
      const count = this.selectedSampleIDs.length
      return this.$t('W: Annuller {count} prøver', { count })
    },
  },

  methods: {
    ...mapActions(useOrderStore, ['cancelOrder', 'cancelOrderSamples']),
    ...mapActions(useUserStore, ['canUser']),

    getSampleLabel,

    isSampleReportLocked(sample: IOrderSample): boolean {
      if (!this.order) return false
      if (getOrderInvoiceLock(this.order, this.invoicesListByOrderID[this.order.id] ?? [])) return true
      const tatGroup = this.order.tatGroups[sample.tatGroupID]
      if (!tatGroup) return false
      return isDeliveryReportLocked(tatGroup.statuses.labReportStatus)
    },

    onToggleAll(): void {
      if (this.allSelected) {
        this.selectedSampleIDs = []
      } else {
        this.selectedSampleIDs = this.cancellableSamples.map(
          (x) => x.sample.sampleID
        )
      }
    },

    onToggleSample(sampleID: string): void {
      if (this.selectedSampleIDs.includes(sampleID)) {
        this.selectedSampleIDs = this.selectedSampleIDs.filter(
          (x) => x !== sampleID
        )
      } else {
        this.selectedSampleIDs = [...this.selectedSampleIDs, sampleID]
      }
    },

    async onSubmit(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        const reason = this.reason.trim() || undefined
        if (this.mode === 'entire') {
          await this.cancelOrder(this.orderID, reason)
          useToast().success(this.$t('W: Ordre annulleret'))
        } else {
          await this.cancelOrderSamples(
            this.orderID,
            this.selectedSampleIDs,
            reason
          )
          const count = this.selectedSampleIDs.length
          useToast().success(
            count === 1
              ? this.$t('W: 1 prøve annulleret')
              : this.$t('W: {count} prøver annulleret', { count })
          )
        }
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        this.$emit('close')
      } catch {
        this.errorText = this.$t('W: Der opstod en fejl')
        useToast().error(this.$t('W: Kunne ikke annullere'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    if (this.hasLockedGroup) {
      this.mode = 'samples'
    }

    // A typed cancellation reason plus the samples picked for a partial cancel. `mode` is
    // deliberately outside the snapshot: switching the pill with both still empty is one
    // click to redo, and `created()` sets it for a locked group anyway.
    this.unsavedGuard.register({
      snapshot: () => ({ reason: this.reason, selectedSampleIDs: this.selectedSampleIDs }),
      modalId: this.modalEntryID,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

